/// The API this build talks to, baked in at build time.
///
/// A production build with no `VITE_API_URL` used to fall back to
/// `http://localhost:8888`. Nothing failed: the build exited 0 and shipped
/// a bundle that pointed every user at a developer's machine. That is
/// exactly what happened — Delivery Boy rendered the variable as a
/// single-command shell prefix, so it never reached `npm run build`, and
/// the panel was broken in production for every release after the
/// migration with a green deploy each time.
///
/// So the fallback is dev-only now. In a production build a missing value
/// is a build error, which is the difference between a deploy that fails
/// and users who can't sign in.
const API_BASE: string = (() => {
  const configured = import.meta.env.VITE_API_URL;
  if (configured) return configured;
  if (import.meta.env.PROD) {
    throw new Error(
      'VITE_API_URL is required for a production build. Without it this bundle ' +
        'would ship pointing at localhost. Set it in apps/panel/.env.production ' +
        'or in the build environment.',
    );
  }
  return 'http://localhost:8888';
})();

/// Default hard timeout for API calls. Without this, a hung server or
/// mid-flight connection loss would leave the UI spinning forever.
const DEFAULT_TIMEOUT_MS = 15_000;

/// Retry policy for transient network failures. Fetch throws an error
/// (not a response) on DNS failures, aborted connections, and TLS errors —
/// those are retryable. HTTP 5xx is also retryable; 4xx is not (the
/// request is malformed or unauthorized).
const MAX_RETRIES = 2;
const INITIAL_RETRY_DELAY_MS = 300;
const MAX_RETRY_DELAY_MS = 2_000;

interface RequestOptions {
  method?: string;
  body?: unknown;
  /// Override the timeout for this specific call.
  timeoutMs?: number;
  /// Disable automatic retries (e.g. for POSTs that are not idempotent).
  noRetry?: boolean;
  /// Skip the automatic refresh-on-401 path. Used by the refresh endpoint
  /// itself so we don't recurse, and by the logout path which has nothing
  /// meaningful to retry.
  skipRefresh?: boolean;
}

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export class TimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms`);
  }
}

/// Hook registered by the auth context so the API client can perform a
/// refresh when it encounters a 401. We keep this as a module-level
/// variable instead of wiring context through every call site.
let onUnauthorized: (() => Promise<boolean>) | null = null;

/// Register the 401-recovery hook. Returns a teardown function so the
/// caller can clear it (mostly useful for tests).
export function setUnauthorizedHandler(handler: (() => Promise<boolean>) | null): void {
  onUnauthorized = handler;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const canRetry = !opts.noRetry && isIdempotent(opts.method ?? 'GET');

  const attempt = async (): Promise<T> => {
    const res = await performFetch(path, opts, timeoutMs);
    return handleResponse<T>(res, path, opts);
  };

  let lastError: unknown;
  const maxAttempts = canRetry ? MAX_RETRIES + 1 : 1;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      return await attempt();
    } catch (err) {
      lastError = err;
      if (!shouldRetry(err, i, maxAttempts)) {
        throw err;
      }
      const delay = computeBackoff(i);
      await sleep(delay);
    }
  }
  throw lastError ?? new Error('Request failed');
}

export async function requestBlob(path: string, opts: RequestOptions = {}): Promise<Blob> {
  const response = await performFetch(path, opts, opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
  return response.blob();
}

async function performFetch(
  path: string,
  opts: RequestOptions,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    return await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      // Include HttpOnly session cookies on every request. The server
      // reads `meridian_session` on the way in and sets it on the way out.
      credentials: 'include',
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new TimeoutError(timeoutMs);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function handleResponse<T>(
  res: Response,
  path: string,
  opts: RequestOptions,
): Promise<T> {
  if (res.status === 401 && onUnauthorized && !opts.skipRefresh) {
    // Try a single refresh-then-retry. The handler returns true if it
    // successfully refreshed the session.
    const refreshed = await onUnauthorized();
    if (refreshed) {
      const retryRes = await performFetch(
        path,
        { ...opts, skipRefresh: true },
        opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      );
      return handleResponse<T>(retryRes, path, { ...opts, skipRefresh: true });
    }
  }

  if (!res.ok) {
    const text = await res.text();
    let message: string;
    try {
      const json = JSON.parse(text);
      message = json.error ?? json.message ?? text;
    } catch {
      message = text || res.statusText;
    }
    throw new ApiError(res.status, message);
  }

  // Some endpoints return 204 No Content — don't try to parse those.
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

function isIdempotent(method: string): boolean {
  const upper = method.toUpperCase();
  return upper === 'GET' || upper === 'HEAD' || upper === 'PUT' || upper === 'DELETE';
}

function shouldRetry(err: unknown, attempt: number, maxAttempts: number): boolean {
  if (attempt + 1 >= maxAttempts) return false;
  if (err instanceof TimeoutError) return true;
  if (err instanceof ApiError) {
    // Only retry 5xx — 4xx means the request itself is wrong.
    return err.status >= 500 && err.status < 600;
  }
  // Network errors thrown by fetch (DNS, TCP reset, TLS) don't have a
  // specific type; retry them.
  return err instanceof TypeError;
}

function computeBackoff(attempt: number): number {
  const exp = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
  const capped = Math.min(exp, MAX_RETRY_DELAY_MS);
  // Add up to 25% jitter to spread out retries.
  const jitter = Math.random() * capped * 0.25;
  return capped + jitter;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
